import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import {
  Card,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableColumn,
} from '@/components/ui';
import { Input } from '@/components/forms';
import { Paginator } from '@/components/ui/Paginator/Paginator';
import { useModalStore } from '@/stores';
import { DeleteConfirmationModal } from '@/components/modals/DeleteConfirmationModal';
import { InfoModal } from '@/components/modals/InfoModal';
import styles from './Providers.module.css';

const PAGE_SIZE = 10;

interface PricingUnit {
  id: string;
  name: string;
  slug: string;
}

export function Pricing() {
  const { openModal, closeModal } = useModalStore();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data: allData, isLoading } = useQuery<PricingUnit[]>({
    queryKey: ['pricing-units'],
    queryFn: async () => {
      const response = await api.get(API_ROUTES.pricingUnits);
      return Array.isArray(response.data) ? response.data : (response.data.results ?? []);
    },
  });

  const filteredData = useMemo(() => {
    if (!allData) return [];
    if (!search.trim()) return allData;
    const s = search.toLowerCase();
    return allData.filter((u) => u.name.toLowerCase().includes(s));
  }, [allData, search]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
  const paginatedData = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = (val: string) => {
    setSearch(val);
    setPage(1);
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${API_ROUTES.pricingUnits}${id}/`);
    },
    onSuccess: () => {
      toast.success('Pricing unit deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['pricing-units'] });
      closeModal();
    },
    onError: () => toast.error('Error deleting pricing unit'),
  });

  const handleDelete = (unit: PricingUnit) => {
    openModal(
      <DeleteConfirmationModal
        title="Delete Pricing Unit"
        itemName={unit.name}
        onConfirm={() => deleteMutation.mutate(unit.id)}
        isDeleting={deleteMutation.isPending}
      />,
    );
  };

  return (
    <div className={styles.container}>
      <Card className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <Button
            variant="primary"
            onClick={() =>
              openModal(
                <InfoModal
                  title="Coming Soon"
                  message="The functionality to add pricing units will be available soon."
                />,
              )
            }
          >
            <FontAwesomeIcon icon={faPlus} /> Add Unit
          </Button>
        </div>

        <div className={styles.filters}>
          <Input
            name="search"
            label="Search"
            placeholder="Search units by name..."
            value={search}
            setValue={handleSearch}
            variant="bordered"
            isClearable
          />
        </div>

        <Table aria-label="Pricing units list" radius={8}>
          <TableHeader>
            <TableColumn>Name</TableColumn>
            <TableColumn>Actions</TableColumn>
          </TableHeader>
          <TableBody isLoading={isLoading}>
            {paginatedData.map((unit) => (
              <TableRow key={unit.id}>
                <TableCell>{unit.name}</TableCell>
                <TableCell>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(unit)}>
                    <FontAwesomeIcon icon={faTrash} /> Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <Paginator page={page} numPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}
